import { useEffect, useRef, useState } from "react";
import { collectionGroup, getDocs, query, where, limit } from "firebase/firestore";
import { db } from "@/lib/firebase";

// Module-level cache so multiple components share results this session
const nameCache = new Map<string, boolean>(); // lowerName -> hasSaleOrTrade
let inflight = new Set<string>();

type CardLike = { name?: string | null };

interface Options {
  // If true, attempt querying name_lower first (if you add that field)
  tryLowerField?: boolean;
  // Max names per batch query (Firestore in operator max = 10)
  batchSize?: number;
  // Hard cap to avoid hammering when a giant list is passed
  hardCap?: number;
}

export function useHighlightCollectionNames(cards: CardLike[] | null | undefined, opts?: Options) {
  const { tryLowerField = true, batchSize = 10, hardCap = 150 } = opts || {};
  const [highlightNames, setHighlightNames] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const list = (cards || []).filter(c => !!c?.name);
    if (!list.length) {
      setHighlightNames(new Set());
      return;
    }

    // Build unique lower names (clamp to hardCap)
    const lowers: string[] = [];
    const seen = new Set<string>();
    for (const c of list) {
      const ln = c!.name!.toLowerCase();
      if (!seen.has(ln)) {
        seen.add(ln);
        lowers.push(ln);
        if (lowers.length >= hardCap) break;
      }
    }

    // Fast path: all cached?
    const uncached = lowers.filter(n => !nameCache.has(n));
    if (!uncached.length) {
      setHighlightNames(new Set(lowers.filter(n => nameCache.get(n))));
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      // Query in chunks; we first try name_lower if requested, else name
      const toProcess = uncached.filter(n => !inflight.has(n));
      toProcess.forEach(n => inflight.add(n));

      const runBatch = async (field: "name_lower" | "name", chunk: string[]) => {
        // Convert to originals if field === 'name' (Firestore field is case-sensitive)
        const values = field === "name" ? chunk.map(n => n) : chunk;
        try {
          const qRef = query(
            collectionGroup(db, "cards"),
            where(field, "in", values),
            limit(50) // defensive; name+in already restricts strongly
          );
            const snap = await getDocs(qRef);
            snap.forEach(docSnap => {
              const d: any = docSnap.data();
              const ln = (d.name ?? "").toLowerCase();
              if (!ln) return;
              const status = (d.status || "").toLowerCase();
              if (
                status === "sale" ||
                status === "trade" ||
                d.for_sale === true ||
                d.for_trade === true
              ) {
                nameCache.set(ln, true);
              } else if (!nameCache.has(ln)) {
                // Only mark false if unseen; might later be found true in another doc
                nameCache.set(ln, false);
              }
            });
        } catch {
          // ignore (index may not exist; fallback path will handle)
        }
      };

      for (let i = 0; i < toProcess.length && !cancelled; i += batchSize) {
        const chunk = toProcess.slice(i, i + batchSize);

        if (tryLowerField) {
          await runBatch("name_lower", chunk);
          // Determine which chunk elements are still uncached (if name_lower not present)
          const stillUnresolved = chunk.filter(n => !nameCache.has(n));
          if (stillUnresolved.length) {
            await runBatch("name", stillUnresolved.map(n => n));
          }
        } else {
          await runBatch("name", chunk);
        }
      }

      // mark any remaining as false if not set
      uncached.forEach(n => {
        if (!nameCache.has(n)) nameCache.set(n, false);
      });
      inflight = new Set<string>(Array.from(inflight).filter(n => !uncached.includes(n)));

      if (!cancelled && mounted.current) {
        const hits = lowers.filter(n => nameCache.get(n));
        setHighlightNames(new Set(hits));
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [cards, tryLowerField, batchSize, hardCap]);

  return { highlightNames, loading };
}
